import { z } from 'zod';

/** Structuur zoals de LLM die teruggeeft bij het parsen van geplakte (Excel-)tekst. */
export const ImportLineSchema = z.object({
  description: z.string().min(1),
  qty: z.number().positive().default(1),
  unit: z.string().nullable().optional(),
  product_code: z.string().nullable().optional(),
  confidence: z.number().min(0).max(1).default(0.5),
  raw_row: z.string().nullable().optional(),
});
export type ImportLine = z.infer<typeof ImportLineSchema>;

export const ImportProductSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  unit: z.string().nullable().optional(),
  product_code: z.string().nullable().optional(),
  lines: z.array(ImportLineSchema),
});
export type ImportProduct = z.infer<typeof ImportProductSchema>;

export const ImportStructureSchema = z.object({
  products: z.array(ImportProductSchema),
  unparsed_rows: z.array(z.object({ raw_row: z.string(), reason: z.string() })).default([]),
  warnings: z.array(z.string()).default([]),
});
export type ImportStructure = z.infer<typeof ImportStructureSchema>;

export const ImportParseBodySchema = z.object({
  text: z.string().min(1, 'Plak eerst tekst').max(200_000),
  expected_products: z.coerce.number().int().min(1).max(200).default(1),
  /** Gezet ⇒ regels worden bij bevestigen toegevoegd aan dit bestaande product i.p.v. nieuwe producten aan te maken. */
  target_product_id: z.coerce.number().int().positive().optional(),
});
export type ImportParseBody = z.infer<typeof ImportParseBodySchema>;

export const ImportSessionSchema = z.object({
  id: z.number().int(),
  user_id: z.number().int(),
  raw_text: z.string(),
  expected_products: z.number().int().nullable(),
  ai_provider: z.string().nullable(),
  ai_model: z.string().nullable(),
  ai_structure: ImportStructureSchema.nullable(),
  ai_usage: z.record(z.unknown()).nullable(),
  status: z.enum(['parsed', 'confirmed', 'failed']),
  target_product_id: z.number().int().nullable().optional(),
  created_product_ids: z.array(z.number().int()).nullable(),
  error: z.string().nullable(),
  created_at: z.string().optional(),
});
export type ImportSession = z.infer<typeof ImportSessionSchema>;

export const ImportConfirmBodySchema = z.object({
  auto_lookup: z.boolean().default(false),
});
export type ImportConfirmBody = z.infer<typeof ImportConfirmBodySchema>;

/** Unit4-controle van productcodes uit een importstructuur (stap 2 van de wizard). */
export const ImportCheckCodesBodySchema = z.object({
  codes: z.array(z.string().trim().min(1).max(50)).max(500),
});
export type ImportCheckCodesBody = z.infer<typeof ImportCheckCodesBodySchema>;

export const ImportCodeInfoSchema = z.object({
  exists: z.boolean(),
  description: z.string().nullable(),
  unit: z.string().nullable(),
  /** Geblokkeerd/inactief/uitlopend in Unit4 */
  blocked: z.boolean(),
  /** Het product is zelf een samenstelling (heeft stuklijstregels) → wordt als genest sub-product geladen */
  is_assembly: z.boolean(),
  /** id van het lokale Klingerator-product met deze Unit4-code, als dat al bestaat */
  local_product_id: z.number().int().nullable(),
});
export type ImportCodeInfo = z.infer<typeof ImportCodeInfoSchema>;

/** Genest sub-product dat bij het bevestigen uit Unit4 is geladen of hergebruikt. */
export const ImportNestedProductSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  unit4_product_code: z.string().nullable(),
  /** true als het product al lokaal bestond en alleen gekoppeld is */
  reused: z.boolean(),
});
export type ImportNestedProduct = z.infer<typeof ImportNestedProductSchema>;

// ---------- Lookup jobs (ai-parser) ----------

export const LookupJobStatusSchema = z.enum(['queued', 'running', 'done', 'failed', 'cancelled']);
export type LookupJobStatus = z.infer<typeof LookupJobStatusSchema>;

export const LookupJobSchema = z.object({
  id: z.number().int(),
  query: z.string(),
  product_id: z.number().int().nullable(),
  line_id: z.number().int().nullable(),
  status: LookupJobStatusSchema,
  topk: z.number().int(),
  result: z.unknown().nullable(),
  error: z.string().nullable(),
  attempts: z.number().int(),
  created_by: z.number().int().nullable(),
  created_at: z.string().optional(),
  started_at: z.string().nullable().optional(),
  finished_at: z.string().nullable().optional(),
});
export type LookupJob = z.infer<typeof LookupJobSchema>;

export const CreateLookupJobBodySchema = z.object({
  query: z.string().trim().min(2).max(1000),
  topk: z.number().int().min(1).max(20).optional(),
  product_id: z.number().int().optional().nullable(),
  line_id: z.number().int().optional().nullable(),
});
export type CreateLookupJobBody = z.infer<typeof CreateLookupJobBodySchema>;
